import React, {forwardRef} from 'react';
import MuiAccordion from '@mui/material/Accordion';
import {styled} from '@mui/material/styles';
import {AccordionDetails} from './AccordionDetails';
import {AccordionSummary} from './AccordionSummary';
import {useId} from '../hooks/useId';
import type {AccordionDetailsProps} from './AccordionDetails';
import type {AccordionGlobalProps} from './Accordion';
import type {AccordionSummaryProps} from './AccordionSummary';
import {excludeComponentProps} from '../utils/excludeComponentProps';

export type AccordionItemProps = AccordionGlobalProps;

const StyledAccordionItem = styled(MuiAccordion, {
    ...excludeComponentProps('rightAligned', 'generatedId', 'dense')
})<AccordionItemProps>(({
    theme, variant, disabled
}) => ({
    padding: 0,
    '&:before': {
        display: 'none'
    },

    '&:first-of-type': {
        borderTopLeftRadius: theme.tokens.FuelBorderRadiusLg,
        borderTopRightRadius: theme.tokens.FuelBorderRadiusLg
    },

    '&:last-of-type': {
        borderBottomLeftRadius: theme.tokens.FuelBorderRadiusLg,
        borderBottomRightRadius: theme.tokens.FuelBorderRadiusLg
    },

    borderTopLeftRadius: theme.tokens.FuelBorderRadiusLg,
    borderTopRightRadius: theme.tokens.FuelBorderRadiusLg,
    borderBottomLeftRadius: theme.tokens.FuelBorderRadiusLg,
    borderBottomRightRadius: theme.tokens.FuelBorderRadiusLg,

    // default
    boxShadow: theme.tokens.FuelShadowMd,

    ...variant === 'outlined' && {
        boxShadow: 'none',
        border: `solid 1px ${theme.tokens.FuelColorBorderLight}`,
        zIndex: 10
    },

    ...variant === 'elevated' && {
        boxShadow: theme.tokens.FuelShadowMd,
        border: undefined
    },

    ...disabled && {
        '&.Mui-disabled': {
            background: theme.tokens.FuelColorGray100
        }
    }
}));

// NOTE: Add possible (AccordionDetails, AccordionSummary) children types to default 'children' types
type AccordionItemChildrenType = AccordionDetailsProps | AccordionSummaryProps | AccordionGlobalProps['children'] | React.ReactChild;
// type AccordionItemChildrenType = React.ReactChildren | undefined;

export const AccordionItem = forwardRef<HTMLDivElement, AccordionItemProps>(({
    children, rightAligned, variant = 'elevated', TransitionProps,
    generatedId: inheritedId, id, dense, disabled, ...props
}, ref) => {
    /**
     *
     * NOTE: Regenerating id
     * This was implemented to cover two cases:
     * (1) the developer wants to specify id's for each and every AccordionItem
     * (2) the developer uses an AccordionItem alone, outside of an Accordion
     *
     */
    const newId = useId(id, 'accordion');
    const generatedId = inheritedId || newId;

    return (
        <StyledAccordionItem
            TransitionProps={{
                timeout: 300,
                ...TransitionProps
            }}
            disabled={disabled}
            ref={ref}
            rightAligned={rightAligned}
            variant={variant}
            {...props}
            disableGutters
            generatedId={generatedId} // passed down
            id={`${generatedId}-item`}
        >
            {React.Children.toArray(children).map<AccordionItemChildrenType>((child) => {
                if (React.isValidElement(child)) {
                    if (child.type === AccordionDetails || child.type === AccordionSummary) {
                        return React.cloneElement(child, {
                            rightAligned,
                            dense,
                            ...child.props, // child props to override cloned props
                            /** Props below this line cannot be overwritten */
                            generatedId
                        });
                    }
                }

                return child;
            })}
        </StyledAccordionItem>
    );
});

AccordionItem.displayName = 'AccordionItem';
